import { Permission, PermissionType } from './../../Models/JWTTokenResponse.model';
import { Injectable, HostListener } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { LoginService } from 'src/app/auth/login.service';
import { map, startWith } from 'rxjs/operators';

// Menu
export interface Menu {
  path?: string;
  title?: string;
  icon?: string;
  type?: string;
  badgeType?: string;
  badgeValue?: string;
  active?: boolean;
  bookmark?: boolean;
  children?: Menu[];
  code?: string;
}

@Injectable({
  providedIn: 'root',
})
export class NavService {
  public screenWidth: any;
  public collapseSidebar: boolean = true;
  public fullScreen = false;
  public items: BehaviorSubject<Menu[]>;
  constructor(private loginService: LoginService) {
    this.onResize();
    if (this.screenWidth < 991) {
      this.collapseSidebar = true;
    }

    // Initialize with current permissions
    const initialUserCodes = this.loginService.getUserCodes();
    const initialFilteredMenuItems = this.filterMenuItems(this.MENUITEMS, initialUserCodes);
    this.items = new BehaviorSubject<Menu[]>(initialFilteredMenuItems);

    // Subscribe to permission changes and update menu accordingly
    this.loginService.userCodes$.pipe(
      startWith(initialUserCodes)
    ).subscribe(userCodes => {
      const filteredMenuItems = this.filterMenuItems(this.MENUITEMS, userCodes);
      this.items.next(filteredMenuItems);
    });
  }

  private filterMenuItems(menuItems: Menu[], userCodes: any[]): Menu[] {
    return menuItems.filter(menuItem => {
      // Check if the menu item should be shown
      const shouldShow = !userCodes.some(userCode =>
        userCode.securityCode === menuItem.code && userCode.permission === PermissionType.Deny
      );

      // If the menu item has children, filter them too
      if (menuItem.children) {
        menuItem.children = this.filterMenuItems(menuItem.children, userCodes);
      }

      return shouldShow;
    });
  }

  // Method to manually refresh menu based on current permissions
  public refreshMenu(): void {
    const userCodes = this.loginService.getUserCodes();
    const filteredMenuItems = this.filterMenuItems(this.MENUITEMS, userCodes);
    this.items.next(filteredMenuItems);
  }

  // Windows width
  @HostListener('window:resize', ['$event'])
  onResize(event?: any) {
    this.screenWidth = window.innerWidth;
  }

  MENUITEMS: Menu[] = [
    {
      title: 'Products',
      icon: 'home',
      type: 'sub',
      active: true,
      code: 'get-products-list',
      children: [
        {
          path: './products/list', title: 'List', type: 'link'
          // , code:'get-products-list'
        },
        {
          path: './products/mapping', title: 'Mapping', type: 'link'
          // , code: 'mapping' 
        },
        {
          path: './products/mapping/brand-management', title: 'Brand Mapping', type: 'link'
        },
        { path: './products/brands', title: 'Brands', type: 'link' },
      ],
    },
    {
      title: 'User Managment',
      icon: 'user',
      type: 'sub',
      active: false,
      code: 'users',
      children: [
        { path: './admin/users', title: 'Users', type: 'link' },
        { path: './admin/organizations', title: 'Organizations', type: 'link' },
      ],
    },
    {
      title: 'Admin',
      icon: 'shield',
      type: 'sub',
      active: false,
      children: [
        { path: './admin/products', title: 'Products', type: 'link', code: 'admin-products-page' },
        { path: './admin/brands', title: 'Brands', type: 'link', code: 'admin-brands-page' },
        { path: './admin/ai-mapping-suggestions', title: 'AI Mapping Suggestions', type: 'link', code: 'admin-ai-suggestions-page' },
        { path: './admin/ai-system-size-suggestions', title: 'AI System Size Suggestions', type: 'link', code: 'admin-ai-suggestions-page' },
        { path: './admin/permissions', title: 'Permissions', type: 'link', code: 'admin-permissions-page' },
        { path: './admin/security-groups', title: 'Security Groups', type: 'link', code: 'admin-security-groups-page' },
      ],
    },
    {
      title: 'Products Listing',
      icon: 'list',
      type: 'link',
      active: false,
      path: './listing',
      code: 'product-listing-functionality'
    },
    {
      title: 'Size Order',
      icon: 'sliders',
      type: 'link',
      active: false,
      path: './size-order',
      code: 'mapping'
    },
    {
      title: 'Size Management',
      icon: 'maximize',
      type: 'link',
      active: false,
      path: './size-management',
      code: 'mapping'
    },
    {
      title: 'Client Integration',
      icon: 'cloud',
      type: 'sub',
      active: false,
      code: 'client-integration',
      children: [
        { path: './clientIntegration/accessToken', title: 'Access Token', type: 'link' },
        // { path: './clientIntegration/Products', title: 'Products', type: 'link' },
      ],
    },
    {
      title: 'Vendor Portal',
      icon: 'package',
      type: 'sub',
      active: false,
      children: [
        { path: './vendor-portal', title: 'Picking', type: 'link', code: 'full-access-vendor-portal' },
        { path: './vendor-portal/history', title: 'History', type: 'link', code: 'vendor-portal-history' }
      ]
    },
    // {
    //   title: 'Order Management',
    //   icon: 'package',
    //   type: 'link',
    //   active: false,
    //   path: './orders',
    //   code: 'orders'
    // },
    {
      title: 'Order Management',
      icon: 'package',
      type: 'link',
      active: false,
      path: './orders-management',
      code: 'order-management'
    },
    {
      title: 'Bulk Order Upload',
      icon: 'package',
      type: 'link',
      active: false,
      path: './bulk-order-upload',
      code: 'orders-create-excel'
    }
  ];
}
